import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import FileGroupingFileSpecVM from './FileGroupingFileSpecVM';
import { observer } from 'mobx-react';
import { TextConstants } from '../../../Common/TextConstants';
import FileGroupingFileSpecLookup from '../Models/Lookups/FileGroupingFileSpecLookup';
import ManageFileGroupingFileSpecComponent from '../Components/ManageFileGroupingFileSpecComponent';
import Loading from '../../../App/Components/Loading/Loading';
import { BrokerFileConfiguration, FileConfigurations, ServiceProviders } from '../../../AccessControl/Models/Commands/Security/Roles';

class FileGroupingFileSpecParams {
    public fileGroupingID = {};
    public broker = {};
    public fileSpecID = {};
    public backURL = {};
}

@observer
export default class FileGroupingFileSpecView extends Views.ViewBase<FileGroupingFileSpecVM, FileGroupingFileSpecParams> {
    public static params = new FileGroupingFileSpecParams();

    public initialise() {
        this.viewModel.initialise(this.viewParams.broker.asString(), Number(this.viewParams.fileGroupingID.value), Number(this.viewParams.fileSpecID.value), String(this.viewParams.backURL.value));
    }

    constructor(props: unknown) {
        super("File Grouping File Spec", FileGroupingFileSpecVM, props);
    }

    public render() {
        return (
            <div>
                {(this.viewModel.taskRunner.isBusy) && <Loading />}
                {this.viewModel.showManageFileGroupingFileSpecComponent &&
                    <ManageFileGroupingFileSpecComponent
                        viewModel={this.viewModel.manageFileGroupingFileSpecComponentVM}
                        onSave={() => this.viewModel.saveFileGroupingFileSpec()}
                        onCancel={() => this.viewModel.cancel()} />
                }

                {!this.viewModel.showManageFileGroupingFileSpecComponent &&
                    <Neo.Card title={TextConstants.Titles.BrokerFileConfiguration} className="m-1">
                        {this.viewModel.authorisationService.hasRole(BrokerFileConfiguration.AddBrokerFileType) &&
                            <div className="row">
                                <div className="col text-right">
                                    <Neo.Button className="btn btn-default" onClick={() => this.viewModel.addNewFileGroupingFileSpec()}
                                        variant="primary"
                                    >{TextConstants.Buttons.AddBrokerFileType}</Neo.Button>
                                </div>
                            </div>
                        }

                        <div>
                            <Neo.Form
                                model={this.viewModel.fileGroupingFileSpecSearchCriteria}
                                onSubmit={() => this.viewModel.fileGroupingFileSpecSearchCriteria.meta.searchCriteria.value === "" ? this.viewModel.pageManager.refreshData() : this.viewModel.clearSearch()}>
                                <div className="row">
                                    <div className="col-lg-3">
                                        <Neo.FormGroup bindContext={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.serviceProviderGroupingName} label={TextConstants.Labels.Broker}>
                                            <Neo.AutoCompleteDropDown
                                                itemSource={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria}
                                                bind={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.staticServiceProviderFileGroupingID}
                                                bindDisplay={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.serviceProviderGroupingName}
                                                displayMember={"serviceProviderGroupingName"}
                                                onItemSelected={() => this.viewModel.pageManager.refreshData()}
                                                items={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ").then((arr) => arr.data
                                                .sort((x,y)=> x.serviceProviderGroupingName.localeCompare(y.serviceProviderGroupingName)))}
                                            />
                                        </Neo.FormGroup>
                                    </div>
                                    <div className="col-lg-3">
                                        <Neo.FormGroup bindContext={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.fileTypeConcatenated} label={TextConstants.Labels.FileType}>
                                            <Neo.AutoCompleteDropDown
                                                itemSource={this.viewModel.fileGroupingFileSpecApiClient.getFileTypeListByCriteria}
                                                bind={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.staticFileSpecID}
                                                bindDisplay={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.fileTypeConcatenated}
                                                displayMember={"propertiesConcatenated"}
                                                onItemSelected={() => this.viewModel.pageManager.refreshData()}
                                                items={this.viewModel.fileGroupingFileSpecApiClient.getFileTypeListByCriteria(" ").then((arr) => arr.data.slice(0, 10)
                                                .sort((x,y)=> x.propertiesConcatenated.localeCompare(y.propertiesConcatenated)))}
                                            />
                                        </Neo.FormGroup>
                                    </div>
                                </div>
                                <Neo.FormGroup
                                    suppressLabel={true}
                                    bind={this.viewModel.fileGroupingFileSpecSearchCriteria.meta.searchCriteria}
                                    placeholder={TextConstants.PlaceHolder.SearchBrokerFileTypes}
                                    append={
                                        <Neo.Button className="btn-small" isSubmit variant="primary">
                                            <i className={`${this.viewModel.fileGroupingFileSpecSearchCriteria.meta.searchCriteria.value === "" ? "fa fa-search" : "fa fa-times-circle"}`} />
                                        </Neo.Button>
                                    }>
                                </Neo.FormGroup>
                            </Neo.Form>
                        </div>
                        <div>
                            <Neo.Pager pageManager={this.viewModel.pageManager} pageControlProps={{ pageSizeLabel: "Show:" }}>
                                <div className="horizontalScroll">
                                    {this.viewModel.pageManager.totalRecords > 0 &&
                                        <NeoGrid.Grid<FileGroupingFileSpecLookup> className={"table-striped"}>
                                            {(item, meta) => (
                                                <NeoGrid.Row key={item.staticServiceProviderFileGroupingFileSpecId}>
                                                    <NeoGrid.Column display={meta.stockbrokerName} label={TextConstants.Labels.Broker} />
                                                    <NeoGrid.Column display={meta.fileType} />
                                                    <NeoGrid.Column display={meta.activeStartDate} />
                                                    <NeoGrid.Column display={meta.activeEndDate} />
                                                    <NeoGrid.Column display={meta.fileNameConvention} />
                                                    <NeoGrid.Column display={meta.ignoreFileInd} label={TextConstants.Labels.IgnoreFile} />
                                                    <NeoGrid.Column display={meta.isCriticalFileInd} label={TextConstants.Labels.CriticalFile} />
                                                    <NeoGrid.Column display={meta.processSortOrder} />
                                                    {this.viewModel.authorisationService.hasRole(BrokerFileConfiguration.Edit) &&
                                                        <NeoGrid.ButtonColumn label={TextConstants.Labels.Edit} className="text-center">
                                                            <Neo.Button
                                                                tooltip={TextConstants.Tooltips.EditBrokerFileConfiguration}
                                                                onClick={() => this.viewModel.editFileGroupingFileSpec(item)}
                                                                variant="primary" isOutline className="btn-small">
                                                                <i className="fa fa-edit"></i>
                                                            </Neo.Button>
                                                        </NeoGrid.ButtonColumn>
                                                    }
                                                    {this.viewModel.authorisationService.hasRole(BrokerFileConfiguration.ViewImportProcesses) &&
                                                        <NeoGrid.ButtonColumn>
                                                            <Neo.Button
                                                                onClick={() => this.viewModel.viewImportProcessesForBroker(item)}
                                                                variant="secondary" isOutline className="btn-link">{TextConstants.Buttons.ViewImportProcesses}</Neo.Button>
                                                        </NeoGrid.ButtonColumn>
                                                    }
                                                </NeoGrid.Row>
                                            )}
                                        </NeoGrid.Grid>
                                    }
                                </div>
                            </Neo.Pager>
                        </div>
                        <div className="row">
                            {String(this.viewParams.backURL.value) !== "null"
                                && this.viewModel.authorisationService.hasRole(ServiceProviders.Access)
                                && this.viewModel.authorisationService.hasRole(FileConfigurations.Access) &&
                                <div className="col">
                                    <Neo.Button className="btn btn-default"
                                        onClick={() => this.viewModel.onBack()}
                                        variant="secondary"
                                    >{TextConstants.Buttons.Back}</Neo.Button>
                                </div>
                            }
                        </div>
                        {String(this.viewParams.backURL.value) === "null" &&
                            <div className="row">
                                {this.viewModel.authorisationService.hasRole(ServiceProviders.Access) &&
                                    <div className="ml-3">
                                        <Neo.Button variant="secondary"
                                            onClick={() => this.viewModel.navigateToServiceProviders()}>{TextConstants.Buttons.BackToServiceProviders}</Neo.Button>
                                    </div>
                                }
                                {this.viewModel.authorisationService.hasRole(FileConfigurations.Access) &&
                                    <div className="ml-3">
                                        <Neo.Button
                                            variant="secondary"
                                            onClick={() => this.viewModel.navigateToFileSpecs()}>{TextConstants.Buttons.BackToFileConfigurations}</Neo.Button>
                                    </div>
                                }
                            </div>
                        }
                    </Neo.Card>
                }
            </div>
        );
    }
}